import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Text from '../common/Text';
import { SEASONS, SeasonDef } from '../../data/landingData';

type ThemeColors = {
  text: string;
  secondary: string;
  card: string;
  border: string;
};

type Props = {
  colors: ThemeColors;
  isMobile: boolean;
};

const SeasonCard: React.FC<{ season: SeasonDef; isMobile: boolean }> = ({
  season,
  isMobile,
}) => (
  <View style={[styles.cardWrapper, isMobile && styles.cardWrapperMobile]}>
    <LinearGradient
      colors={season.gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[styles.card, isMobile && styles.cardMobile]}
    >
      {/* Season name */}
      <Text style={[styles.seasonName, { color: season.textColor }]}>
        {season.name}
      </Text>
      <Text style={[styles.seasonLabel, { color: season.textColor }]}>
        {season.label}
      </Text>

      {/* Swatch row */}
      <View style={styles.swatchRow}>
        {season.swatches.map((hex, i) => (
          <View
            key={i}
            style={[
              styles.swatch,
              { backgroundColor: hex },
              Platform.OS === 'web'
                ? ({ boxShadow: `0 2px 6px ${hex}66` } as object)
                : {
                    shadowColor: hex,
                    shadowOpacity: 0.4,
                    shadowRadius: 4,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 3,
                  },
            ]}
          />
        ))}
      </View>

      {/* Description */}
      <Text style={[styles.description, { color: season.textColor }]}>
        {season.description}
      </Text>
    </LinearGradient>
  </View>
);

const SeasonShowcaseSection: React.FC<Props> = ({ colors, isMobile }) => {
  return (
    <View style={[styles.section, isMobile && styles.sectionMobile]}>
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text style={[styles.eyebrow, { color: colors.secondary }]}>
          Your Season Awaits
        </Text>
        <Text
          style={[
            styles.title,
            isMobile && styles.titleMobile,
            { color: colors.text },
          ]}
        >
          Every Skin Tone Has a Season
        </Text>
        <Text
          style={[
            styles.subtitle,
            isMobile && styles.subtitleMobile,
            { color: colors.secondary },
          ]}
        >
          Colour analysis places you in one of four seasonal palettes — each
          with shades uniquely designed to flatter you.
        </Text>
      </View>

      <View style={[styles.grid, isMobile && styles.gridMobile]}>
        {SEASONS.map((season) => (
          <SeasonCard key={season.name} season={season} isMobile={isMobile} />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  sectionMobile: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 32,
    width: '100%',
    flexGrow: 0,
  },
  header: {
    alignItems: 'center',
    marginBottom: 28,
  },
  headerMobile: {
    marginBottom: 18,
  },
  eyebrow: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 34,
    marginBottom: 8,
  },
  titleMobile: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    opacity: 0.8,
    maxWidth: 560,
  },
  subtitleMobile: {
    fontSize: 14,
    lineHeight: 21,
    maxWidth: undefined,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
  },
  gridMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    width: '100%',
    flexGrow: 0,
    gap: 16,
  },
  cardWrapper: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 220,
    maxWidth: 280,
  },
  cardWrapperMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    minWidth: 0,
    maxWidth: 99999,
    width: '100%',
    alignSelf: 'stretch',
  },
  card: {
    borderRadius: 24,
    padding: 24,
    minHeight: 240,
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  cardMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    minHeight: 0,
    padding: 16,
    width: '100%',
    alignSelf: 'stretch',
  },
  seasonName: {
    fontSize: 26,
    fontWeight: '900',
    letterSpacing: -0.5,
    marginBottom: 2,
  },
  seasonLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    opacity: 0.75,
    marginBottom: 20,
  },
  swatchRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  description: {
    fontSize: 13,
    lineHeight: 19,
    opacity: 0.85,
  },
});

export default SeasonShowcaseSection;
